/* eslint-disable import/no-extraneous-dependencies */
import React, { useCallback, useContext, useEffect } from 'react';
import type { MessageDescriptor } from 'react-intl-next';
import { FormattedMessage, defineMessages } from 'react-intl-next';

import AutoDismissFlag from '@atlaskit/flag/auto-dismiss-flag';

import { SPAViewContext } from '@confluence/spa-view-context';
import {
	EDIT_PAGE_PUBLISH_EXPERIENCE,
	FRONT_COVER_PUBLISH_EXPERIENCE,
	ExperienceTrackerContext,
} from '@confluence/experience-tracker';
import {
	isCompanyHubSpaceKey,
	FrontCoverStateEnum,
} from '@confluence/route-manager/entry-points/companyHubUtils';
import type { FlagsStateContainer } from '@confluence/flags';
import type { DialogsStateContainer } from '@confluence/dialogs';
import { useIsProductAdmin } from '@confluence/current-user';
import { useSaveCompanyHub } from '@confluence/company-hub/entry-points/useSaveCompanyHub';
import {
	useChangeCompanyHubPublishState,
	GraphQLFrontCoverState,
} from '@confluence/company-hub-utils/entry-points/useChangeHubPublishState';

const UNAUTHORIZED_ERROR_MESSAGE = 'Current user does not have permission to update site settings';

const i18n = defineMessages({
	publishSuccessTitle: {
		id: 'editor-features.success.front-cover.publish.title',
		description: 'Success title message when a hub is published',
		defaultMessage: '{companyHubName} is now active',
	},
	updateSuccessTitle: {
		id: 'editor-features.success.front-cover.update.title',
		description: 'Success title message when a hub is updated',
		defaultMessage: '{companyHubName} has been updated',
	},
	publishSuccessDescription: {
		id: 'editor-features.user.publish.front-cover.success',
		description: 'Success message when publishing the hub',
		defaultMessage: 'This hub is now visible to everyone. Make updates to it anytime.',
	},
	updateSuccessDescription: {
		id: 'editor-features.user.update.front-cover.success',
		description: 'Success message when updating the hub',
		defaultMessage: 'Your recent update is now visible to everyone.',
	},
	publishErrorTitle: {
		id: 'editor-features.front-cover.publish.error-title',
		description: 'Title for error flag when a user publishes in-editor but cannot enable their hub',
		defaultMessage: 'Something went wrong',
	},
	publishErrorDescription: {
		id: 'editor-features.publish.front-cover.error-message',
		description:
			'Description for error flag when a user publishes in-editor but cannot enable their hub',
		defaultMessage:
			'Due to an error, this landing page failed to publish. Reload and publish it again.',
	},
});

export const usePublishCompanyHub = (
	spaceKey: string,
	contentId: string,
	flags?: FlagsStateContainer,
	dialogs?: DialogsStateContainer,
	refetchNativeCollab?: (shouldCheckCache: boolean) => Promise<any>,
) => {
	const experienceTracker = useContext(ExperienceTrackerContext);
	const { frontCoverState, companyHubName } = useContext(SPAViewContext);
	const { isProductAdmin } = useIsProductAdmin();

	const showSuccessFlag = useCallback(() => {
		let title: MessageDescriptor;
		let description: MessageDescriptor;
		if (frontCoverState === FrontCoverStateEnum.SHOWN) {
			title = i18n.updateSuccessTitle;
			description = i18n.updateSuccessDescription;
		} else {
			title = i18n.publishSuccessTitle;
			description = i18n.publishSuccessDescription;
		}
		void flags?.showFlag({
			type: 'success-circle',
			title: <FormattedMessage {...title} values={{ companyHubName }} />,
			description: <FormattedMessage {...description} />,
			close: 'never', // XXX Works around hideAllFlags in EditorFlags!
			customComponent: AutoDismissFlag,
		});
	}, [flags, companyHubName, frontCoverState]);
	const showErrorFlag = useCallback(() => {
		void flags?.showFlag({
			type: 'error',
			title: <FormattedMessage {...i18n.publishErrorTitle} />,
			description: <FormattedMessage {...i18n.publishErrorDescription} />,
			close: 'never', // XXX Works around hideAllFlags in EditorFlags!
		});
	}, [flags]);

	const { changeHubPublishState } = useChangeCompanyHubPublishState();
	const { getSuccessModalDialogData } = useSaveCompanyHub();

	const publishCompanyHub = useCallback(async () => {
		const data = refetchNativeCollab ? await refetchNativeCollab(true) : {};
		const versionFromQuery = data?.content?.nodes?.[0]?.version?.number ?? 1;

		experienceTracker.start({
			name: FRONT_COVER_PUBLISH_EXPERIENCE,
			attributes: {
				contentId,
				isFirstPublish: versionFromQuery === 1,
			},
		});

		try {
			const { data } = await changeHubPublishState(GraphQLFrontCoverState.SHOWN);
			if (
				data?.updateSiteLookAndFeel.siteLookAndFeel?.frontCoverState !== FrontCoverStateEnum.SHOWN
			) {
				const isUnauthorizedError = data?.updateSiteLookAndFeel.errors?.some((error) =>
					error?.message?.includes(UNAUTHORIZED_ERROR_MESSAGE),
				);
				if (isUnauthorizedError) {
					experienceTracker.abort({
						name: FRONT_COVER_PUBLISH_EXPERIENCE,
						reason: UNAUTHORIZED_ERROR_MESSAGE,
					});
					return;
				} else {
					const errors = data?.updateSiteLookAndFeel.errors || [];
					const errorMessages = errors.map((error) => error.message).join('.');
					throw new Error(errorMessages || 'An unknown error occurred');
				}
			}

			experienceTracker.succeed({
				name: FRONT_COVER_PUBLISH_EXPERIENCE,
			});
		} catch (error) {
			experienceTracker.stopOnError({
				name: FRONT_COVER_PUBLISH_EXPERIENCE,
				error,
			});
			throw error;
		}
	}, [contentId, experienceTracker, changeHubPublishState, refetchNativeCollab]);

	const onPublishSuccess = useCallback(async () => {
		const isCompanyHubPublished = frontCoverState === FrontCoverStateEnum.SHOWN;

		try {
			if (!isCompanyHubPublished && isProductAdmin) {
				await publishCompanyHub();
			}
		} catch (error) {
			showErrorFlag();
			return;
		}

		const successModalDialogData = await getSuccessModalDialogData(contentId);

		if (successModalDialogData.isVisible) {
			dialogs?.showModal(successModalDialogData.modalDialog, {
				publicationStatus: isCompanyHubPublished ? 'updated' : 'published',
				companyHubName,
				announcementBannerId: successModalDialogData.bannerId,
			});
		} else {
			showSuccessFlag();
		}
	}, [
		dialogs,
		frontCoverState,
		isProductAdmin,
		publishCompanyHub,
		showErrorFlag,
		showSuccessFlag,
		companyHubName,
		getSuccessModalDialogData,
		contentId,
	]);

	useEffect(() => {
		if (isCompanyHubSpaceKey(spaceKey)) {
			return experienceTracker.subscribe((event) => {
				if (
					event.name === EDIT_PAGE_PUBLISH_EXPERIENCE &&
					event.action === 'taskSuccess' &&
					event.attributes?.isCompanyHub
				) {
					void onPublishSuccess();
				}
			});
		}
	}, [contentId, experienceTracker, spaceKey, onPublishSuccess]);
};
